/**
 * BetterAuth REST client (P1 — real login + multi-org).
 *
 * Thin typed wrappers over the server mounted at `/api/betterauth` (see backend
 * src/web/src/lib/betterauth.ts). Same-origin, cookie-session; the API bearer
 * JWT is minted by GET /token from the cookie session and carries the active
 * org + app role in its signed payload.
 */

const AUTH_BASE = `${(import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''}/api/betterauth`;

export interface AuthUser {
  id: string;
  name: string;
  email: string;
}

export interface AuthSessionInfo {
  user: AuthUser;
  activeOrganizationId: string | null;
}

export interface OrgSummary {
  id: string;
  name: string;
  slug: string;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<{ ok: boolean; status: number; data: T | null; error: string | null }> {
  try {
    const res = await fetch(`${AUTH_BASE}${path}`, {
      credentials: 'include',
      headers: init.body ? { 'Content-Type': 'application/json' } : undefined,
      ...init,
    });
    let data: T | null = null;
    let error: string | null = null;
    const text = await res.text();
    if (text) {
      try {
        const parsed = JSON.parse(text) as T & { message?: string };
        if (res.ok) data = parsed;
        else error = parsed.message ?? text;
      } catch {
        error = text;
      }
    }
    if (!res.ok && !error) error = `${res.status}`;
    return { ok: res.ok, status: res.status, data, error };
  } catch (e) {
    return { ok: false, status: 0, data: null, error: String(e) };
  }
}

/**
 * ZERO-PII sign-up: we deliberately do NOT collect or store a name — the `name`
 * column is written as ''. The e-mail is the unavoidable login identifier; UI
 * display handles are derived client-side and never persisted.
 */
export async function signUpEmail(email: string, password: string): Promise<string | null> {
  const r = await call('/sign-up/email', { method: 'POST', body: JSON.stringify({ name: '', email, password }) });
  return r.ok ? null : (r.error ?? 'sign-up failed');
}

/** Social login (OAuth). Returns the provider redirect URL to navigate to. */
export async function signInSocial(provider: string): Promise<{ url: string | null; error: string | null }> {
  const r = await call<{ url?: string }>('/sign-in/social', {
    method: 'POST',
    body: JSON.stringify({ provider, callbackURL: '/app/today' }),
  });
  return { url: r.ok ? (r.data?.url ?? null) : null, error: r.ok ? null : (r.error ?? 'social sign-in failed') };
}

export async function signInEmail(email: string, password: string): Promise<string | null> {
  const r = await call('/sign-in/email', { method: 'POST', body: JSON.stringify({ email, password }) });
  return r.ok ? null : (r.error ?? 'sign-in failed');
}

export async function signOut(): Promise<void> {
  await call('/sign-out', { method: 'POST', body: '{}' });
}

export async function getSession(): Promise<AuthSessionInfo | null> {
  const r = await call<{ user?: AuthUser; session?: { activeOrganizationId?: string | null } }>('/get-session');
  if (!r.ok || !r.data?.user) return null;
  return { user: r.data.user, activeOrganizationId: r.data.session?.activeOrganizationId ?? null };
}

export async function listOrgs(): Promise<OrgSummary[]> {
  const r = await call<OrgSummary[]>('/organization/list');
  return r.ok && Array.isArray(r.data) ? r.data : [];
}

export async function createOrg(name: string, slug: string): Promise<{ org: OrgSummary | null; error: string | null }> {
  const r = await call<OrgSummary>('/organization/create', { method: 'POST', body: JSON.stringify({ name, slug }) });
  return { org: r.ok ? r.data : null, error: r.ok ? null : (r.error ?? 'could not create organization') };
}

export async function setActiveOrg(organizationId: string): Promise<boolean> {
  const r = await call('/organization/set-active', { method: 'POST', body: JSON.stringify({ organizationId }) });
  return r.ok;
}

/** The caller's role in the ACTIVE org (owner | admin | member), from the membership table. */
export async function activeMemberRole(): Promise<string | null> {
  const r = await call<{ role?: string }>('/organization/get-active-member-role');
  return r.ok ? (r.data?.role ?? null) : null;
}

export interface OrgMember {
  id: string;
  role: string;
  userId: string;
  user: { name: string; email: string };
}

/** Full active organization incl. members — powers Settings → Users & the people pickers. */
export async function getFullOrganization(): Promise<{ members: OrgMember[] } | null> {
  const r = await call<{ members?: OrgMember[] }>('/organization/get-full-organization');
  if (!r.ok || !r.data) return null;
  return { members: r.data.members ?? [] };
}

export async function inviteMember(email: string, role: 'member' | 'admin'): Promise<string | null> {
  const r = await call('/organization/invite-member', { method: 'POST', body: JSON.stringify({ email, role }) });
  return r.ok ? null : (r.error ?? 'invite failed');
}

/** Short-lived API bearer (EdDSA JWT; active org + app role in the payload). */
export async function fetchToken(): Promise<string | null> {
  const r = await call<{ token?: string }>('/token');
  return r.ok ? (r.data?.token ?? null) : null;
}
